import { useEffect, useRef, type ReactNode } from 'react';
import { V2CloseButton } from '@sakhtar/design-system/react';

/**
 * A wide panel for create/edit forms and record detail (Sales CRM design:
 * «فرم ایجاد در صفحه یا پنل مستقل»), opened from the inline-end edge so the
 * list's name column stays visible behind it. Esc and the backdrop close
 * it; when `dirty`, closing asks first so typed data is never lost silently.
 */
export function SidePanel({
  open,
  title,
  subtitle,
  onClose,
  dirty = false,
  width = 520,
  footer,
  children,
}: {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  dirty?: boolean;
  width?: number;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Read through refs so the Esc handler (bound once per open) always uses
  // the latest `dirty` and `onClose`.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  function requestClose() {
    if (dirtyRef.current && !window.confirm('تغییرات ذخیره‌نشده از بین می‌رود. پنل بسته شود؟')) {
      return;
    }
    onCloseRef.current();
  }

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <>
      <div className="sk-drawer-backdrop" onClick={requestClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="sk-side-panel"
        style={{
          position: 'fixed',
          insetBlock: 0,
          insetInlineEnd: 0,
          width: `min(${width}px, 100vw)`,
          background: 'var(--sk-bg-elev)',
          boxShadow: 'var(--sk-shadow-4)',
          zIndex: 'var(--sk-z-modal)' as unknown as number,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '16px 20px',
            borderBottom: '1px solid var(--sk-divider)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--sk-fg)' }}>{title}</span>
            {subtitle ? (
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>{subtitle}</span>
            ) : null}
          </div>
          <V2CloseButton onClick={requestClose} />
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
        {footer ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              padding: '14px 20px',
              borderTop: '1px solid var(--sk-divider)',
              background: 'var(--sk-bg-canvas)',
            }}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </>
  );
}
